import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Paper, TextField, Alert, CircularProgress, Stack } from '@mui/material';
import { authAPI } from '../services/api';

const WelcomeSetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('This link is invalid. Please ask your manager for a new invite.');
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      await authAPI.setPassword(token, password);
      setSuccess('Your password has been set. You can now log in.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Unable to set password. This link may have expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const linkInvalid = !token;

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <img
            src="/sglogo.svg"
            alt="SuperGauge logo"
            style={{ maxWidth: 160, height: 'auto' }}
          />
        </Box>
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Welcome to SuperGauge
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Set your password to activate your account and start completing surveys.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {linkInvalid ? (
          <Typography variant="body2" color="text.secondary">
            This link is invalid or may have expired. Please ask your manager to resend your SuperGauge invite.
          </Typography>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, textAlign: 'left' }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <TextField
                fullWidth
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Set password and continue'}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default WelcomeSetPassword;
